import { IntlShape } from 'react-intl'
import { Table, flexRender } from '@tanstack/react-table'
import { PaymentTermin } from '../../../../interfaces/Termin/Termin'
import { Column } from '@tanstack/react-table'
import { TempPaymentTermin } from '../../../../interfaces/Termin/Termin'
import { getPinningStyles } from '../../../../functions/general'
import { renderWithouFlexRender, renderWithFlexRender } from '../../../../functions/general'
import { currencyFormatter, getCurrencySymbol } from '../../../../functions/general'
import { FormikProps } from 'formik'
import { Pipeline } from '../../../../interfaces'
import PreviewPaidSchedule from '../PreviewPaidSchedule'
import { CustomField, SettingMultiPipelines } from '../../../../interfaces/Settings'
import { Switch } from '@mui/material'
import clsx from 'clsx';
import { DatePicker } from 'rsuite'
import useTodayRange from '../../../../hooks/TodayRange'
import InputAttachmentTermin from '../InputAttachmentTermin'
import RenderCustomFieldGeneralForm from '../../../RenderComponent/RenderCustomFieldGeneralForm'


interface MainContentTerminProps {
    switchPaidSchedule: boolean
    pipeline: Pipeline
    errorTermin: boolean
    intl: IntlShape
    table: Table<PaymentTermin>
    unpaymentNominal: number
    selectMultiPipeline: SettingMultiPipelines
    formik: FormikProps<any>
    customFieldTermin: CustomField
    handleNominalChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleVATTerminChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleDateChange: (date: Date | null) => void
    isAfterToday: (date: Date) => boolean
    isBeforeToday: (date: Date) => boolean
    terminErrors: any
    handleAddPaymentTermin: () => void
}

const MainContentTermin = ({ switchPaidSchedule, pipeline, errorTermin, intl, table, unpaymentNominal, selectMultiPipeline, formik, customFieldTermin, handleNominalChange, handleVATTerminChange, handleDateChange, isAfterToday, isBeforeToday, terminErrors, handleAddPaymentTermin }: MainContentTerminProps) => {
    const todayRange = useTodayRange();

    return (
        <>
            {
                switchPaidSchedule ?
                    <PreviewPaidSchedule pipeline={pipeline} />
                    : errorTermin ?
                        <div className="alert alert-danger">
                            {intl.formatMessage({ id: "FORM.VALIDATION.ERROR_LOADING_DATA" })}
                        </div>
                        :
                        <>
                            <div className="table-responsive">
                                <table className="table table-row-dashed table-row-gray-230 align-middle gs-0 gy-4">
                                    <thead>
                                        <tr className="fw-bolder text-dark" style={{ backgroundColor: '#D4D4D4' }}>
                                            {table.getHeaderGroups().map(headerGroup => (
                                                headerGroup.headers.map((header, index) => (
                                                    <th
                                                        key={header.id}
                                                        style={{
                                                            padding: `12px ${header.column.columnDef.size}px`,
                                                            whiteSpace: 'nowrap',
                                                            minWidth: 'fit-content',
                                                            ...getPinningStyles(header.column as Column<TempPaymentTermin | PaymentTermin>, true)
                                                        }}
                                                        className="align-middle"
                                                    >
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                    </th>
                                                ))
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map(row => (
                                            <tr key={row.id}>
                                                {row.getVisibleCells().map((cell, index) => (
                                                    <td
                                                        key={cell.id}
                                                        style={{
                                                            padding: `12px ${cell.column.columnDef.size}px`,
                                                            whiteSpace: 'nowrap',
                                                            minWidth: 'fit-content',
                                                            ...getPinningStyles(cell.column as Column<TempPaymentTermin | PaymentTermin>, false)
                                                        }}
                                                        className="align-middle"
                                                    >
                                                        {index === 0 ? renderWithouFlexRender(cell) : renderWithFlexRender(cell)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {
                                unpaymentNominal > 0 && (
                                    <div className="mt-5">
                                        <div className='d-flex flex-column gap-3'>
                                            <h5>{intl.formatMessage({ id: "FORM.LABEL.CREATE_NEW_TERMIN" })}</h5>
                                            <h6>{intl.formatMessage({ id: "FORM.LABEL.REMAINING_UNPAYMENT" }) + ` (${selectMultiPipeline.currencyCode})`} : {currencyFormatter(unpaymentNominal, selectMultiPipeline.currencyIsRounded, selectMultiPipeline.currencyCode, { showPrefix: true })}</h6>
                                            {
                                                formik.values.inputType === 'percentage' &&
                                                <h6>{intl.formatMessage({ id: "FORM.LABEL.REMAINING_UNPAYMENT_PERCENTAGE" }, { title: `${unpaymentNominal / pipeline.nominal * 100}` })}</h6>
                                            }
                                        </div>
                                        <div className="row g-3 mt-3">
                                            <div className='col-md-3'>
                                                <div className="form-group">
                                                    <label className="form-label fs-6 fw-bolder text-dark">{intl.formatMessage({ id: "FORM.LABEL.INPUT_TYPE" })}</label>
                                                    <select
                                                        className="form-select"
                                                        value={formik.values.inputType}
                                                        onChange={(e) => formik.setFieldValue('inputType', e.target.value)}
                                                    >
                                                        <option value="nominal">Nominal</option>
                                                        <option value="percentage">Persentase</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className='col-md-3'>
                                                <div className="form-group h-100">
                                                    <div className="d-flex align-items-center h-100 pt-6">
                                                        <label className='form-label fs-6 fw-bolder text-dark mb-0 me-3'>{intl.formatMessage({ id: 'FORM.ACTION.ALREADY_PAID' })}</label>
                                                        <Switch
                                                            checked={formik.values.togglePayment}
                                                            onChange={(e) => formik.setFieldValue('togglePayment', e.target.checked)}
                                                            inputProps={{ 'aria-label': 'secondary checkbox' }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='col-md-6'></div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label fs-6 fw-bolder text-dark required">
                                                        {formik.values.inputType === 'nominal' ? intl.formatMessage({ id: "FORM.LABEL.PAYMENT_AMOUNT" }) : intl.formatMessage({ id: "FORM.LABEL.PAYMENT_PERCENTAGE" })} ({selectMultiPipeline.currencyCode})
                                                    </label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">
                                                            {formik.values.inputType === 'nominal' ? getCurrencySymbol(selectMultiPipeline.currencyCode) : '%'}
                                                        </span>
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            className={clsx('form-control', {
                                                                'is-invalid': terminErrors.nominal,
                                                            })}
                                                            placeholder={formik.values.inputType === 'nominal' ? intl.formatMessage({ id: "FORM.LABEL.ENTER_PAYMENT_AMOUNT" }, { title: 'Nominal' }) : intl.formatMessage({ id: "FORM.LABEL.ENTER_PERCENTAGE" })}
                                                            value={formik.values.nominal}
                                                            onChange={handleNominalChange}
                                                        />
                                                    </div>
                                                    {terminErrors.nominal && (
                                                        <div className="invalid-feedback d-block">
                                                            {terminErrors.nominal}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6 col-lg-2">
                                                <div className="form-group">
                                                    <label className="form-label fs-6 fw-bolder text-dark ">
                                                        {intl.formatMessage({ id: "FORM.LABEL.VAT" })}
                                                    </label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">%</span>
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            className="form-control"
                                                            value={formik.values.vatPercentage}
                                                            onChange={handleVATTerminChange}
                                                        />
                                                    </div>
                                                    {terminErrors.vatPercentage && (
                                                        <div className="invalid-feedback d-block">
                                                            {terminErrors.vatPercentage}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-6 col-lg-4">
                                                <div className="form-group">
                                                    {
                                                        formik.values.togglePayment ? (
                                                            <>
                                                                <label className="form-label fs-6 fw-bolder text-dark required">
                                                                    {intl.formatMessage({ id: "FORM.LABEL.PAYMENT_DATE" })}
                                                                </label>
                                                                <DatePicker
                                                                    value={formik.values.datePaid ? new Date(formik.values.datePaid) : null}
                                                                    onChange={handleDateChange}
                                                                    ranges={todayRange}
                                                                    oneTap
                                                                    editable={false}
                                                                    format="dd MMM yyyy"
                                                                    style={{ width: "100%" }}
                                                                    className={clsx('form-control p-1', {
                                                                        'is-invalid': terminErrors.datePaid,
                                                                    })}
                                                                    shouldDisableDate={isAfterToday}
                                                                    container={document.querySelector(`#detail-termin-pipeline-modal-${pipeline.id}`) as HTMLElement}
                                                                />
                                                                {terminErrors.datePaid && (
                                                                    <div className="invalid-feedback d-block">
                                                                        {terminErrors.datePaid}
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <label className="form-label fs-6 fw-bolder text-dark required">
                                                                    {intl.formatMessage({ id: "FORM.LABEL.DEADLINE_PAYMENT" })}
                                                                </label>
                                                                <DatePicker
                                                                    value={formik.values.dateDue ? new Date(formik.values.dateDue) : null}
                                                                    onChange={handleDateChange}
                                                                    ranges={todayRange}
                                                                    oneTap
                                                                    editable={false}
                                                                    format="dd MMM yyyy"
                                                                    style={{ width: "100%" }}
                                                                    className={clsx('form-control p-1', {
                                                                        'is-invalid': terminErrors.dateDue,
                                                                    })}
                                                                    shouldDisableDate={isBeforeToday}
                                                                    container={document.querySelector(`#detail-termin-pipeline-modal-${pipeline.id}`) as HTMLElement}
                                                                />
                                                                {terminErrors.dateDue && (
                                                                    <div className="invalid-feedback d-block">
                                                                        {terminErrors.dateDue}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                            <div className='col-md-12'>
                                                {formik.values.togglePayment &&
                                                    <div className="col-md-8">
                                                        <label className="form-label fs-6 fw-bolder text-dark">{intl.formatMessage({ id: 'FORM.LABEL.PROOF_OF_PAYMENT_TERM' })}</label>
                                                        <InputAttachmentTermin key={`formik.receiptUrls`} keyString={`formik.receiptUrls`} formik={formik} isMulti={false} type="input" accept="image/png, image/jpeg, image/jpg" typeCustomField='paymentTermin' />
                                                    </div>
                                                }
                                            </div>

                                            <RenderCustomFieldGeneralForm
                                                customFieldGeneral={customFieldTermin}
                                                formik={formik}
                                                errors={formik.errors}
                                                containerRef={document.querySelector(`#detail-termin-pipeline-modal-${pipeline.id}`) as HTMLElement}
                                            />
                                        </div>
                                        <button className='btn btn-primary w-100 mt-3' onClick={handleAddPaymentTermin}>
                                            <i className='fa fa-plus'></i>
                                            Tambah Termin
                                        </button>
                                    </div>
                                )
                            }
                        </>
            }
        </>
    )
}

export default MainContentTermin